import { useTranslation } from 'react-i18next'

import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useCurrentSession } from '~/hooks/useCurrentSession'
import { resolveIntradayApproverStatus } from '~/pages/IntradayBidding/models/approverStatus'

/**
 * 承認者判定 (AC-BIN-098) の対象の札。 一覧 RPC の行 (`IntradayOrderRow` / `IntradayBidRow`)
 * も詳細 RPC の値で埋めた札もこの形で渡せる。
 */
export type IntradayApproverTarget = {
  /**
   * 札が今待っている承認段階の承認者 member_id。 backend が承認の実処理と同じ
   * 「承認申請時に写した段」 から返す (一覧 ・ 詳細の RPC の `current_stage_approver_member_ids`)。
   * 空は「承認者: 設定なし」。
   */
  currentStageApproverMemberIds?: readonly string[]
  /** 申請者 (`requested_by`、 REQUEST 遷移時の操作者)。 */
  requestedBy?: string
}

export type IntradayApproverContext = {
  /**
   * 現在ユーザーが、 札が今待っている承認段階の承認者に含まれないと確定できるか
   * (AC-BIN-098)。 現在ユーザーの読み込み中は false (= 未確定の判定を確定情報として
   * 出さない)。 読み込みが終われば `resolveIntradayApproverStatus` の fail-closed な判定
   * (取得失敗 = 承認者でない) をそのまま返す。
   */
  isNotApprover: (target: IntradayApproverTarget) => boolean
  /**
   * 申請者が現在ユーザー本人か (AC-BIN-098 後段の自己承認)。 申請者が無い札と、
   * 現在ユーザーの読み込み中 ・ 取得失敗の間は false (= fail-closed)。
   */
  isSelfApplicant: (target: IntradayApproverTarget) => boolean
  /** 承認者名の表示 (承認者ゼロ件は「設定なし」)。 */
  approverListLabel: (target: IntradayApproverTarget) => string
  /**
   * 承認/差戻しの操作口の近傍に常時出す注意 (AC-BIN-098 (a) の承認者非該当と、 後段の
   * 自己承認)。 該当しなければ空。 入札一覧の操作列と詳細モーダルが同じ文言を出す。
   */
  persistentNotices: (target: IntradayApproverTarget) => string[]
}

/**
 * 時間前入札の承認者判定 (AC-BIN-098)。 現在ユーザー (#6641 の member_id) と、 札が
 * 運ぶ今の承認段階の承認者 (承認申請時に写した段) を突合する。 承認者を札から読むので、
 * 申請後に承認フロー設定を変えても、 警告の対象と実際に承認できる人が食い違わない
 * (`models/approverStatus.ts`)。 詳細モーダル ・ 入札一覧の操作列 ・ 承認 hook が同じ
 * 判定と文言を使う。
 *
 * `useCurrentSession` は取得失敗時 (`isError`) に直前の `data` を保持したまま
 * `isPending` を false にするため、 `isError` の間は member_id を undefined 扱いにして
 * fail-closed 判定に乗せる (組織切り替え直後の再取得失敗で旧 member_id を拾わない、
 * `useCurrentSession.ts` JSDoc)。
 */
export const useIntradayApproverContext = (): IntradayApproverContext => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const {
    data: sessionData,
    isPending: isSessionPending,
    isError: isSessionError,
  } = useCurrentSession()
  const currentMemberId = isSessionError ? undefined : sessionData?.memberId

  const approverIdsOf = (target: IntradayApproverTarget): readonly string[] =>
    target.currentStageApproverMemberIds ?? []
  const isNotApprover = (target: IntradayApproverTarget): boolean =>
    !isSessionPending &&
    !resolveIntradayApproverStatus(approverIdsOf(target), currentMemberId)
      .isApprover
  const isSelfApplicant = (target: IntradayApproverTarget): boolean =>
    target.requestedBy !== undefined && target.requestedBy === currentMemberId
  const approverListLabel = (target: IntradayApproverTarget): string => {
    const approverMemberIds = approverIdsOf(target)
    return approverMemberIds.length > 0
      ? approverMemberIds.map((id) => resolveDisplayName(id) ?? '—').join('、')
      : t('page:biddingIntraday.approvalAction.notApproverNoApprovers')
  }

  return {
    isNotApprover,
    isSelfApplicant,
    approverListLabel,
    // 承認者でなく申請者本人でもある時は、 2 本に分けず 1 文にまとめる (alert-placement §3)。
    persistentNotices: (target) => {
      const notApprover = isNotApprover(target)
      const self = isSelfApplicant(target)
      if (notApprover && self) {
        return [
          t(
            'page:biddingIntraday.approvalAction.notApproverAndSelfPersistent',
            {
              approvers: approverListLabel(target),
            },
          ),
        ]
      }
      return [
        ...(notApprover
          ? [
              t('page:biddingIntraday.approvalAction.notApproverPersistent', {
                approvers: approverListLabel(target),
              }),
            ]
          : []),
        ...(self
          ? [t('page:biddingIntraday.approvalAction.selfApprovalWarning')]
          : []),
      ]
    },
  }
}
